import { useState } from 'react';
import toast from 'react-hot-toast';

import type { UserTeamId } from '../types';
import { Popup } from '../Popup';
import { General, Media } from '../StrokeIcons';
import ButtonLink from './ButtonLink';

interface OpenElectronButtonProps {
  userTeam: UserTeamId;
}

export const OpenElectronButton = ({ userTeam }: OpenElectronButtonProps) => {
  const userId = userTeam.userId;
  const teamId = userTeam.teamId;
  const deepLinkUrl = `useposeidon://run?userId=${userId}&teamId=${teamId}`;
  const [isChecking, setIsChecking] = useState(false);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Check if we're on localhost - deep links won't work there
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      e.preventDefault();

      navigator.clipboard
        .writeText(deepLinkUrl)
        .then(() => {
          toast.success('Deep link URL copied to clipboard. Paste it in a browser to open the desktop app.');
        })
        .catch(() => {
          toast.error('Failed to copy deep link URL to clipboard');
        });
      return;
    }

    // Detect if app opens successfully using blur detection
    e.preventDefault();
    setIsChecking(true);

    let blurred = false;
    const onBlur = () => {
      blurred = true;
    };

    window.addEventListener('blur', onBlur);

    // Try to open the deep link
    window.location.href = deepLinkUrl;

    // Check if window lost focus (app opened) after a short delay
    setTimeout(() => {
      window.removeEventListener('blur-sm', onBlur);
      setIsChecking(false);

      if (!blurred) {
        // Window didn't blur, app probably not installed - show download popup
        setShowDownloadPopup(true);
      }
    }, 1000);
  };

  return (
    <>
      <ButtonLink
        href={deepLinkUrl}
        size="sm"
        icon={<General.Download01 />}
        tooltip="Run Desktop agent"
        onClick={handleClick}
        disabled={isChecking}>
        {isChecking ? 'Opening...' : 'Open Desktop Agent'}
      </ButtonLink>

      {showDownloadPopup && (
        <Popup onClose={() => setShowDownloadPopup(false)} className="max-w-2xl">
          <div className="text-left">
            {/* Header */}
            <div className="bg-linear-to-br from-indigo-700 to-indigo-900 p-6 -m-5 mb-4 rounded-t-lg text-white">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center size-14 [&>svg]:size-8 text-white">
                  <Media.Monitor03 />
                </div>
                <div>
                  <h2 className="text-2xl font-light">Download Desktop App</h2>
                  <p className="text-indigo-100 text-sm">Install the app to run automation from this page</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                The desktop app helps you scale your professional engagement with advanced automation and scheduling
                features.
              </p>

              {/* Download Options */}
              <div className="space-y-3">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <General.Apple className="size-6 shrink-0" />
                      <div className="shrink">
                        <p className="font-medium text-gray-900 dark:text-gray-100">macOS</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Universal app for Intel & Apple Silicon
                        </p>
                      </div>
                    </div>
                    <ButtonLink
                      className="shrink-0"
                      href="https://storage.googleapis.com/poseidon-electron-build/latest/Poseidon.dmg"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowDownloadPopup(false)}>
                      Download
                    </ButtonLink>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Requires macOS 11.0 or later • Works natively on M1/M2/M3 Macs
                  </p>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <General.Windows className="size-6 shrink-0" />
                      <div className="shrink">
                        <p className="font-medium text-gray-900 dark:text-gray-100">Windows</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">PC version (.exe)</p>
                      </div>
                    </div>
                    <ButtonLink
                      className="shrink-0"
                      href="https://storage.googleapis.com/poseidon-electron-build/latest/Poseidon.exe"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowDownloadPopup(false)}>
                      Download
                    </ButtonLink>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Requires Windows 10 version 1903 or later • 64-bit processor • 4GB RAM minimum
                  </p>
                </div>
              </div>

              {/* Help text */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>After installing:</strong> Open the desktop app, log in with your account, then click "Open
                  Desktop Agent" on this page to start automation.
                </p>
              </div>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};
